import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, MessageSquare, IndianRupee, CheckCircle2, Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [tab, setTab] = useState("stats");

  useEffect(() => {
    if (user?.role !== "admin") return;
    Promise.all([
      http.get("/admin/stats").then((r) => setStats(r.data)),
      http.get("/admin/users").then((r) => setUsers(r.data)),
      http.get("/admin/feedback").then((r) => setFeedback(r.data)),
    ]).catch(() => {});
  }, [user]);

  if (loading) return <div className="text-center py-24"><Loader2 className="w-8 h-8 animate-spin text-forest mx-auto" /></div>;
  if (user?.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="animate-fade-in">
      <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Admin Panel</h1>
      <p className="text-muted-foreground mb-6">Signed in as {user.email}</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Users, label: "Total users", val: stats.users_total },
            { icon: CheckCircle2, label: "Active subs", val: stats.active_subscribers },
            { icon: IndianRupee, label: "Paid orders", val: stats.orders_paid },
            { icon: MessageSquare, label: "Open feedback", val: stats.feedback_open },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="p-5 bg-white border-forest/10">
                <Icon className="w-5 h-5 text-forest mb-2" />
                <p className="font-heading text-3xl font-semibold text-forest">{s.val}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["users", "feedback"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm border ${
              tab === t ? "bg-forest text-bone border-forest" : "border-forest/20 hover:border-forest/40"
            }`}
          >{t === "users" ? "Users" : "Feedback"}</button>
        ))}
      </div>

      {tab === "users" && (
        <Card className="p-4 bg-white border-forest/10">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-forest/10">
                  <th className="py-2 px-2">Email</th>
                  <th className="py-2 px-2">Name</th>
                  <th className="py-2 px-2">Role</th>
                  <th className="py-2 px-2">Sub status</th>
                  <th className="py-2 px-2">Expires</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-forest/5">
                    <td className="py-2 px-2">{u.email}</td>
                    <td className="py-2 px-2">{u.name}</td>
                    <td className="py-2 px-2">
                      <Badge className={u.role === "admin" ? "bg-honey text-forest" : "bg-bone text-forest"}>{u.role}</Badge>
                    </td>
                    <td className="py-2 px-2">{u.subscription_status || "trial"}</td>
                    <td className="py-2 px-2 text-xs">{u.subscription_expires_at ? new Date(u.subscription_expires_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "feedback" && (
        <div className="space-y-3">
          {feedback.length === 0 ? (
            <p className="text-muted-foreground text-sm">No feedback yet.</p>
          ) : feedback.map((f) => (
            <Card key={f.feedback_id} className="p-5 bg-white border-forest/10">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-forest text-bone">{f.category}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(f.created_at).toLocaleString()}</span>
                {f.email && <span className="text-xs text-muted-foreground">· {f.email}</span>}
              </div>
              <p className="text-sm whitespace-pre-wrap">{f.message}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
